<template>
  <div
    class="my-report-card">
    <v-container
      fluid
      class="mx-0"
      style="padding: 18px !important;">
      <div class="report-filter__main-row">
        <div class="report-filter__fields">
          <Autocomplete
            :label="$t('reports.filter.status')"
            v-model="filterMyReports.status"
            :items="myReportsStatusFilter"
            :search-input.sync="statusSearch"
            @change="statusSearch = null"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
          />

          <Select
            :label="$t('reports.filter.period')"
            v-model="selectedDate"
            :items="dateList"
            v-bind="autocompleteAttrs"
          />

          <div v-show="selectedDate === 'custom'">
            <v-menu
              v-if="selectedDate === 'custom'"
              ref="menu"
              v-model="menu"
              offset-y
              max-width="350px"
              min-width="auto"
              :close-on-content-click="false"
              :return-value.sync="dates">
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on">
                  <Textfield
                    :label="$t('reports.filter.dates')"
                    v-model="dateRangeText"
                    readonly
                    hide-details
                    v-bind="textfieldAttrs"
                    class="fill-width">
                    <template slot="append">
                      <v-img
                        src="icons/input-calendar.svg"
                        style="max-height: 22px;"
                        contain
                      />
                    </template>
                  </Textfield>
                </div>
              </template>

              <v-date-picker
                width="auto"
                range
                v-model="dates"
                no-title
                color="primary"
                :allowed-dates="allowedDates"
                :locale="$i18n.locale">
                <v-row class="mx-2">
                  <v-col
                    cols="12"
                    align="end">
                    <v-btn
                      text
                      color="primary"
                      @click="menu = false">
                      {{ $t('reports.filter.buttons.cancel') }}
                    </v-btn>

                    <v-btn
                      color="primary"
                      @click="handlerDate(dates)">
                      {{ $t('reports.filter.buttons.ok') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-date-picker>
            </v-menu>
          </div>

          <Autocomplete
            v-if="showFinancialGroupFilter"
            :label="$t('reports.filter.financialGroup')"
            v-model="filterMyReports.financialGroupId"
            :items="financialGroups"
            :search-input.sync="financialGroupIdSearch"
            @change="financialGroupIdSearch = null"
            item-text="name"
            item-value="id"
            chips
            small-chips
            v-bind="autocompleteAttrs"
          />
        </div>

        <div class="report-filter__actions">
          <v-btn
            outlined
            color="base-color"
            class="report-filter__action"
            @click="cleanFilter()">
            <v-icon left size="20">mdi-broom</v-icon>
            {{ $t('reports.filter.clear') }}
          </v-btn>

          <v-btn
            color="primary"
            class="report-filter__action"
            @click="handlerFilter()">
            <div class="mr-2" style="max-width: 18px;">
              <v-icon
                size="18"
                color="base-color">
                fas fa-search
              </v-icon>
            </div>
            {{ $t('reports.filter.search') }}
          </v-btn>
        </div>
      </div>
    </v-container>

    <SnackbarCustomize ref="SnackbarCustomize" />
  </div>
</template>

<script>
import { AUTOCOMPLETE_ATTRS, TEXTFIELD_ATTRS} from "@/shared/constants/default/Inputs";
import { mapGetters } from 'vuex';

import DatePickerCustom from "@/components/DashboardMovementRecords/Common/DatePickerCustom.vue";
import MovementRecordsMixin from "@/shared/mixins/movementRecords/movementRecordsMixin";
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import MyReportsMixin from "@/shared/mixins/myReports/myReportsMixin";
import ReportMixin from '@/shared/mixins/report/reportMixin';
import Autocomplete from "../Default/Autocomplete.vue";
import Textfield from "../Default/Textfield.vue";
import Select from "../Default/Select.vue";
import moment from 'moment';
export default {
	name: "ReportFilter",
  components: {
    SnackbarCustomize,
    DatePickerCustom,
    Autocomplete,
    Textfield,
    Select,
  },
	data: () => ({
    menu: false,
    selectedDate: 'lastThirtyDays',
    dates: [],
		financialGroupIdSearch: null,
    statusSearch: null,
		reportLayoutSearch: null,
	}),
	mixins: [
    ReportMixin,
    MovementRecordsMixin,
    MyReportsMixin
  ],
  props: {
		resetFilterMyReports: {
			type: Boolean,
			default: false
		},
    filtersForCheckDate: Object,
    borderSelected: {
      type: Boolean,
      default: true,
    },
    isAdmissionMapReport: {
      type: Boolean,
      default: false,
    },
    hideFinancialGroupFilter: {
      type: Boolean,
      default: false,
    },
	},
  watch: {
		filterMyReports: {
			deep: true,
			immediate: true,
			handler(value) {
				if (value) this.$emit("hasFiltersMyReports", this.filterMyReports);
			}
		},
		resetFilterMyReports: {
			deep: true,
			handler(value) {
				if (value) this.clearFilterMyReports()
			}
		},
    selectedDate: {
      handler() {
        this.setDateFilter(this.selectedDate);
        this.$emit('updateDates', this.dates);
      }
    }
	},
	methods: {
    expand() {
			if (this.filterExpanded === 0) {
				this.filterExpanded = undefined;

        return;
			}

      this.filterExpanded = 0;
		},
    handlerFilter() {
      if (this.selectedDate === 'noDate') {
        const hasFinancialGroup = !!this.filterMyReports.financialGroupId;
        const hasStatus = !!this.filterMyReports.status;
        const isInvalidNoDateFilter = this.isAdmissionMapReport || this.hideFinancialGroupFilter
          ? !hasStatus
          : !hasFinancialGroup || hasStatus;

        if (isInvalidNoDateFilter) {
          this.dates = [];
          this.$refs.SnackbarCustomize.open('error', this.$t('reports.filter.snackbar.fillField'));
          return;
        }
      }
      this.setDateFilter(this.selectedDate)
      this.$emit('handlerClickFilter', this.dates);
    },
    setDateFilter(value) {
      const today = moment();
      const dateRanges = {
        lastSevenDays: 6,
        lastThirtyDays: 29,
        lastThreeMonths: 89,
        currentMonth: today.clone().startOf('day').diff(today.clone().startOf('month').startOf('day'), 'days'),
        noDate: 'noDate',
      };
      let startDate;
      if (value === 'noDate') {
        this.dates = [];
        startDate = [];
      } else if (dateRanges[value] !== undefined) {
        startDate = today.clone().subtract(dateRanges[value], 'days');
        this.dates = [startDate.format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
      }
    },
    cleanFilter() {
      this.selectedDate = 'lastThirtyDays';
      this.dates = [];
      this.$emit('clearFilters');
    },
    clearFilterMyReports() {
			this.filterMyReports = {
				financialGroupId: '',
				reportLayout: '',
				status: '',
				receivingDateStart: '',
				receivingDateEnd: '',
			}
			this.$forceUpdate()
			this.$emit("resetStatusFilters", false)
		},
    handlerDate(dates) {
      this.menu = false;
      const dateFormated = dates;
      if (dateFormated[0] > dateFormated[1]) {
        [dateFormated[0], dateFormated[1]] = [dateFormated[1], dateFormated[0]];
      }
      this.$refs.menu.save(dateFormated);
    },
    allowedDates(date) {
      const selectedDate = moment(date);
      if (
        typeof this.dates === 'undefined' ||
        this.dates === null
      ) {
        this.dates = [];
      }
      if (this.dates.length === 0) {
        return true;
      }
      const startDate = moment(this.dates[0]);
      const minDate = startDate.clone().subtract(365, 'days');
      const currentDate = moment();
      return (
        selectedDate.isBetween(minDate, startDate.add(365, 'days'), 'day', '[]')
        && selectedDate.isSameOrBefore(currentDate, 'day')
      );
    },
	},
  computed: {
    ...mapGetters({
      financialGroups: 'financial-group/groups',
    }),
    autocompleteAttrs() {
      return {
        ...AUTOCOMPLETE_ATTRS,
        borderSelected: this.borderSelected,
        hideDetails: true,
        placeholder: this.$t('reports.filter.placeholders.select'),
      };
    },
    textfieldAttrs() {
      return {
        ...TEXTFIELD_ATTRS,
        borderSelected: this.borderSelected,
        placeholder: this.$t('reports.filter.placeholders.select'),
      };
    },
    showFinancialGroupFilter() {
      return !this.isAdmissionMapReport && !this.hideFinancialGroupFilter;
    },
    dateList() {
      return [
        {
          text: this.$t('reports.filter.dateOptions.lastSevenDays'),
          value: 'lastSevenDays',
          disabled: false,
        },
        {
          text: this.$t('reports.filter.dateOptions.lastThirtyDays'),
          value: 'lastThirtyDays',
          disabled: false,
        },
        {
          text: this.$t('reports.filter.dateOptions.lastThreeMonths'),
          value: 'lastThreeMonths',
          disabled: false,
        },
        {
          text: this.$t('reports.filter.dateOptions.currentMonth'),
          value: 'currentMonth',
          disabled: false,
        },
        {
          text: this.$t('reports.filter.dateOptions.custom'),
          value: 'custom',
          disabled: false,
        },
        {
          text: this.$t('reports.filter.dateOptions.noDate'),
          value: 'noDate',
          disabled: false,
        },
        {
          text: this.$t('reports.filter.dateOptions.custom'),
          value: 'custom',
          disabled: false,
        },
      ];
    },
    filterCounter() {
      const {
        receivingDateStart,
        receivingDateEnd,
        ...filter
      } = this.filterMyReports;

      let counter = Object.values(filter).filter(value =>
        value !== null &&
        value !== undefined &&
        !(typeof value === 'string' && value.trim() === '') &&
        !(Array.isArray(value) && value.length === 0)
      ).length;

      if (this.selectedDate !== 'noDate') counter++;

      return counter;
    },
    dateRangeText() {
      let text = '-';
      if (this.dates && this.dates.length > 0) {
        const sortedDates = this.dates.slice().sort((a, b) => new Date(a) - new Date(b));
        const formattedStartDate = moment(sortedDates[0]).format('DD/MM/YYYY');
        const formattedEndDate = moment(sortedDates[1]).format('DD/MM/YYYY');
        text = `${formattedStartDate} ~ ${formattedEndDate}`;
      }
      return text;
    },
    expansionIconTransition() {
			return {
        transform: this.filterExpanded !== 0 ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.25s',
      };
		},
  },
  mounted() {
    this.handlerFilter();
  },
}
</script>

<style lang="scss">
.report-expansion-content > .v-expansion-panel-content__wrap {
  padding: 0;
}

.report-filter__main-row {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  width: 100%;
}

.report-filter__fields {
  display: grid;
  flex: 1 1 720px;
  gap: 20px;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  min-width: 0;

  .v-input {
    margin: 0 !important;
    padding: 0 !important;
  }
}

.report-filter__actions {
  align-items: flex-end;
  display: flex;
  flex: 0 0 auto;
  gap: 20px;
  justify-content: flex-end;
  width: 350px;

  .v-btn {
    margin: 0 !important;
  }
}

.report-filter__action {
  border-radius: 6px !important;
  height: 50px !important;
  min-width: 165px !important;

  &--search {
    color: var(--v-foreground-accent-base) !important;

    .v-btn__content,
    .v-btn__content * {
      color: inherit !important;
    }
  }
}

@media screen and (max-width: 1500px) {
  .report-filter__actions {
    justify-content: flex-end;
    width: 100%;
  }
}

@media screen and (max-width: 1100px) {
  .report-filter__fields {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
}

@media screen and (max-width: 600px) {
  .report-filter__fields {
    grid-template-columns: 1fr;
  }

  .report-filter__actions {
    flex-direction: column;
  }

  .report-filter__action {
    width: 100%;
  }
}
</style>
